import type { TableColumn } from '@libs/front/components/data-table/data-table.component';
import type { HolesView } from '../blast-holes.types';

/**
 * Visible columns of each Holes tab table, remembered in the browser.
 *
 * The choice belongs to the user: once a selection is stored for a view it is used as is on
 * every visit and nothing in the tab changes it automatically (a new default column, a chip,
 * a metric...). Only the user, through the column picker, updates it.
 */

const STORAGE_KEY_PREFIX = 'explore.holes-tab.columns.';

/**
 * Stored selection of `view`, restricted to the keys of `columns` (a stored key whose column no
 * longer exists is dropped). Falls back to `defaultKeys` when nothing usable is stored.
 */
export function readColumnKeys(view: HolesView, columns: readonly TableColumn[], defaultKeys: readonly string[]): Set<string> {
  const known = new Set(columns.map(column => column.key));
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY_PREFIX + view) ?? 'null');
    if (Array.isArray(stored)) {
      const keys = stored.filter((key): key is string => typeof key === 'string' && known.has(key));
      if (keys.length) return new Set(keys);
    }
  } catch {
    // Unreadable storage or corrupt value: use the defaults
  }
  return new Set(defaultKeys);
}

/** Remembers the user's selection for `view` (silently skipped when storage is unavailable). */
export function storeColumnKeys(view: HolesView, keys: ReadonlySet<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + view, JSON.stringify([...keys]));
  } catch {
    // Private mode / storage disabled: the selection simply is not remembered
  }
}
